import { buildAudioSources, LOCALIZED_AUDIO_KEYS, normalizeAudioLanguage, resultAudioKeys } from '../domain/audioLogic.js';
import { runtimeLogger } from './runtimeLogger.js';
import { releaseAssetUrl } from './runtimeAssets.js';

export class GameAudioManager {
  constructor({ language = 'zh', AudioClass = globalThis.Audio } = {}) {
    this.language = normalizeAudioLanguage(language);
    this.AudioClass = AudioClass;
    this.elements = new Map();
    this.muted = false;
    this.queueGeneration = 0;
    this.queueTail = Promise.resolve(false);
    this.queueCurrent = null;
  }

  setLanguage(language) {
    const nextLanguage = normalizeAudioLanguage(language);
    if (nextLanguage === this.language) return;
    this.stopAll();
    this.language = nextLanguage;
    this.elements.forEach((audio, key) => {
      audio.__wanliSources = buildAudioSources(key, this.language).map(releaseAssetUrl);
      this.applySource(audio, 0);
    });
  }

  setMuted(muted) {
    this.muted = Boolean(muted);
    if (this.muted) this.stopAll();
  }

  applySource(audio, index) {
    const sources = audio?.__wanliSources || [];
    if (!sources[index]) return false;
    audio.__wanliSourceIndex = index;
    if (audio.src !== new URL(sources[index], window.location.href).href) {
      audio.src = sources[index];
      try { audio.load?.(); } catch (error) { runtimeLogger.error('[wanli-audio] source load failed', { key: audio.__wanliKey, error }); }
    }
    return true;
  }

  advanceSource(audio) {
    return this.applySource(audio, (Number(audio?.__wanliSourceIndex) || 0) + 1);
  }

  ensure(key) {
    if (!this.AudioClass) return null;
    const sources = buildAudioSources(key, this.language).map(releaseAssetUrl);
    if (!sources.length) return null;
    if (this.elements.has(key)) return this.elements.get(key);
    const audio = new this.AudioClass();
    audio.preload = 'auto';
    audio.playsInline = true;
    audio.__wanliKey = key;
    audio.__wanliSources = sources;
    audio.__wanliSourceIndex = 0;
    this.applySource(audio, 0);
    this.elements.set(key, audio);
    return audio;
  }

  prime(keys = ['click', 'tick', ...LOCALIZED_AUDIO_KEYS]) {
    keys.forEach((key) => {
      const audio = this.ensure(key);
      try { audio?.load?.(); } catch (error) { runtimeLogger.error('[wanli-audio] preload failed', { key, error }); }
    });
  }

  reset(audio) {
    if (!audio) return;
    try { audio.pause?.(); } catch (error) { runtimeLogger.error('[wanli-audio] pause failed', error); }
    try { audio.currentTime = 0; } catch (error) { runtimeLogger.error('[wanli-audio] rewind failed', error); }
  }

  async start(key) {
    if (this.muted) return false;
    const audio = this.ensure(key);
    if (!audio) return false;
    const attempt = async () => {
      this.reset(audio);
      audio.muted = false;
      audio.volume = 1;
      try {
        await audio.play?.();
        return true;
      } catch (error) {
        if (this.advanceSource(audio)) return attempt();
        runtimeLogger.info('[wanli-audio] playback blocked or unavailable', { key });
        return false;
      }
    };
    return attempt();
  }

  play(key, { queue = LOCALIZED_AUDIO_KEYS.includes(key) } = {}) {
    if (this.muted) return Promise.resolve(false);
    if (!queue) return this.start(key);
    const generation = this.queueGeneration;
    this.queueTail = Promise.resolve(this.queueTail).catch(() => false).then(() => {
      if (this.muted || generation !== this.queueGeneration) return false;
      const audio = this.ensure(key);
      if (!audio) return false;
      return new Promise((resolve) => {
        let settled = false;
        const finish = (value) => {
          if (settled) return;
          settled = true;
          audio.removeEventListener?.('ended', ended);
          if (this.queueCurrent?.audio === audio) this.queueCurrent = null;
          resolve(value);
        };
        const ended = () => finish(true);
        this.queueCurrent = { audio, finish };
        audio.addEventListener?.('ended', ended);
        this.start(key).then((started) => { if (!started) finish(false); });
      });
    });
    return this.queueTail;
  }

  playResult(resultCode, total) {
    return resultAudioKeys(resultCode, total).map((key) => this.play(key));
  }

  stopAll() {
    this.queueGeneration += 1;
    this.queueTail = Promise.resolve(false);
    this.queueCurrent?.finish?.(false);
    this.queueCurrent = null;
    this.elements.forEach((audio) => this.reset(audio));
  }

  destroy() {
    this.stopAll();
    this.elements.clear();
  }
}

export function createGameAudio(options) {
  return new GameAudioManager(options);
}
